import { createContext, useContext, useEffect, useState } from 'react';
import type { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: string | null; convertedAnonymous?: boolean }>;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signInWithGoogle: () => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: string | null }>;
  updatePassword: (password: string) => Promise<{ error: string | null }>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const getAuthRedirectUrl = (path: string) => {
  const basePath = __BASE_PATH__ === '/' ? '' : __BASE_PATH__.replace(/\/$/, '');
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${window.location.origin}${basePath}${normalizedPath}`;
};

const getErrorMessage = (error: unknown, fallback: string) => (
  error instanceof Error ? error.message : fallback
);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  const ensureProfile = async (u: User) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .upsert(
          { id: u.id, email: u.email ?? '', plan: 'free' },
          { onConflict: 'id', ignoreDuplicates: true }
        );
      if (error) console.error('Failed to ensure auth profile', error);
    } catch (error) {
      console.error('Failed to ensure auth profile', error);
    }
  };

  const ensureProfileAfterAuthEvent = (u: User) => {
    window.setTimeout(() => {
      void ensureProfile(u);
    }, 0);
  };

  useEffect(() => {
    let mounted = true;

    supabase.auth.getSession()
      .then(({ data: { session: s } }) => {
        if (!mounted) return;
        setSession(s);
        setUser(s?.user ?? null);
        if (s?.user) ensureProfileAfterAuthEvent(s.user);
      })
      .catch((error) => {
        console.error('Failed to restore auth session', error);
        if (mounted) {
          setSession(null);
          setUser(null);
        }
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    const { data: listener } = supabase.auth.onAuthStateChange((event, s) => {
      setSession(s);
      setUser(s?.user ?? null);
      if (event === 'SIGNED_IN' && s?.user) {
        ensureProfileAfterAuthEvent(s.user);
      }
    });

    return () => {
      mounted = false;
      listener.subscription.unsubscribe();
    };
  }, []);

  const signUp = async (email: string, password: string, fullName: string): Promise<{ error: string | null; convertedAnonymous?: boolean }> => {
    const { data: { session: currentSession } } = await supabase.auth.getSession();
    const currentUser = currentSession?.user;

    if (currentUser?.is_anonymous) {
      const { data, error } = await supabase.auth.updateUser(
        {
          email,
          password,
          data: { full_name: fullName },
        },
        { emailRedirectTo: getAuthRedirectUrl('/dashboard/history') }
      );
      if (error) return { error: error.message };

      const upgradedUser = data.user ?? currentUser;
      await supabase
        .from('profiles')
        .update({ email })
        .eq('id', upgradedUser.id);
      setUser(upgradedUser);
      return { error: null, convertedAnonymous: true };
    }

    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: fullName },
        emailRedirectTo: getAuthRedirectUrl('/dashboard/history'),
      },
    });
    if (error) return { error: error.message };
    return { error: null };
  };

  const signIn = async (email: string, password: string): Promise<{ error: string | null }> => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return { error: error.message };
    return { error: null };
  };

  const signInWithGoogle = async (): Promise<{ error: string | null }> => {
    try {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      if (currentSession?.user?.is_anonymous) {
        const { error } = await supabase.auth.linkIdentity({
          provider: 'google',
          options: {
            redirectTo: getAuthRedirectUrl('/dashboard/history'),
          },
        });
        if (error) return { error: error.message };
        return { error: null };
      }

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: getAuthRedirectUrl('/dashboard'),
        },
      });
      if (error) return { error: error.message };
      return { error: null };
    } catch (error) {
      return { error: getErrorMessage(error, 'Unable to start Google sign-in.') };
    }
  };

  const signOut = async () => {
    // Clear state immediately so UI updates right away
    setUser(null);
    setSession(null);

    // Sign out from Supabase (invalidates server-side session)
    await supabase.auth.signOut({ scope: 'local' });

    // Explicitly purge all Supabase auth tokens from localStorage
    // to prevent the session from being restored on next page load
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith('sb-')) {
        localStorage.removeItem(key);
      }
    });
  };

  const resetPassword = async (email: string): Promise<{ error: string | null }> => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: getAuthRedirectUrl('/reset-password'),
      });
      if (error) return { error: error.message };
      return { error: null };
    } catch (error) {
      return { error: getErrorMessage(error, 'Unable to send password reset email.') };
    }
  };

  const updatePassword = async (password: string): Promise<{ error: string | null }> => {
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) return { error: error.message };
      return { error: null };
    } catch (error) {
      return { error: getErrorMessage(error, 'Unable to update password.') };
    }
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signUp, signIn, signInWithGoogle, signOut, resetPassword, updatePassword }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
